import { useEffect, useState } from "react";
import { API } from "./api";
import ReportDashboard from "./components/ReportDashboard";
import { motion, AnimatePresence } from "framer-motion";
import AILoader from "./components/AILoader";
import LeadGate from "./components/LeadGate";
import SuccessScreen from "./components/SuccessScreen";
import { mapAIReport } from "./components/ReportMapper";
import Turnstile from "./components/Turnstile";

const isMobile = window.innerWidth < 640;

export default function App() {
  const STORAGE_KEY = "ai-growth-scan";

  const [questions, setQuestions] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [sections, setSections] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [answers, setAnswers] = useState({});
  const [otherInputs, setOtherInputs] = useState({});
  const [report, setReport] = useState(null);
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [isRestoring, setIsRestoring] = useState(true);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const [company, setCompany] = useState({
    companyName: "",
    name: "",
    email: "",
    mobile: "",
    website: "",
    challenges: ""
  });

  const [errors, setErrors] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const handleStepClick = (index) => {
    // Prevent skipping ahead without validation
    if (index > step) {
      const valid = validate();
      if (!valid) return;
    }

    setStep(index);
    saveToStorage({ step: index });
  };

  const saveToStorage = (dataOverride = {}) => {
    if (!isHydrated || isRestoring) return;

    setSaveStatus("saving");

    const data = {
      company,
      answers,
      otherInputs,
      step,
      questions,
      ...dataOverride
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    setTimeout(() => {
      setSaveStatus("saved");
    }, 400);
  };

  // -----------------------------
  // LOAD + RESTORE (FIXED)
  // -----------------------------
  useEffect(() => {
    const init = async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);

        // ✅ STEP 1: Load instantly from localStorage (if exists)
        if (saved) {
          const parsed = JSON.parse(saved);

          setCompany(parsed.company || {});
          setAnswers(parsed.answers || {});
          setOtherInputs(parsed.otherInputs || {});
          setStep(parsed.step || 0);

          if (parsed.questions?.length) {
            setQuestions(parsed.questions);

            const groupedData = parsed.questions.reduce((acc, q) => {
              acc[q.section] = acc[q.section] || [];
              acc[q.section].push(q);
              return acc;
            }, {});

            setGrouped(groupedData);
            setSections(["Company Details", ...Object.keys(groupedData)]);
          }
        }

        // ✅ UI READY instantly
        setIsHydrated(true);
        setIsRestoring(false);

        // ✅ STEP 2: Background fetch (non-blocking)
        fetchQuestionsInBackground();
      } catch (err) {
        console.error("Init failed", err);
        setIsHydrated(true);
        setIsRestoring(false);
      }
    };

    init();
  }, []);

  const fetchQuestionsInBackground = async () => {
    try {
      const res = await API.get("/questions");
      const data = res.data || [];

      // 🔥 Only update if changed (prevents flicker)
      if (JSON.stringify(data) === JSON.stringify(questions)) return;

      const groupedData = data.reduce((acc, q) => {
        acc[q.section] = acc[q.section] || [];
        acc[q.section].push(q);
        return acc;
      }, {});

      setQuestions(data);
      setGrouped(groupedData);
      setSections(["Company Details", ...Object.keys(groupedData)]);

      // ✅ Update storage silently
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          company,
          answers,
          otherInputs,
          step,
          questions: data
        })
      );
    } catch (err) {
      console.warn("Background refresh failed (safe to ignore)", err);
    }
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;

      try {
        const parsed = JSON.parse(e.newValue);

        setCompany(parsed.company || {});
        setAnswers(parsed.answers || {});
        setOtherInputs(parsed.otherInputs || {});
        setStep(parsed.step || 0);

        if (parsed.questions?.length) {
          setQuestions(parsed.questions);

          const groupedData = parsed.questions.reduce((acc, q) => {
            acc[q.section] = acc[q.section] || [];
            acc[q.section].push(q);
            return acc;
          }, {});

          setGrouped(groupedData);
          setSections(["Company Details", ...Object.keys(groupedData)]);
        }
      } catch (err) {
        console.error("Sync error", err);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // -----------------------------
  // SINGLE SAVE EFFECT (FIXED)
  // -----------------------------
  useEffect(() => {
    if (!isHydrated || isRestoring) return; // 🔥 BLOCK early overwrite

    if (!questions.length) return;

    const data = {
      company,
      answers,
      otherInputs,
      step,
      questions
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [company, answers, otherInputs, step, questions, isHydrated, isRestoring]);

  // -----------------------------
  // HANDLERS
  // -----------------------------
  const handleSelect = (id, value) => {
    const updated = {
      ...answers,
      [id]: value
    };

    setAnswers(updated);
    saveToStorage({ answers: updated });
  };

  const handleOther = (id, value) => {
    const updated = {
      ...otherInputs,
      [id]: value
    };

    setOtherInputs(updated);
    saveToStorage({ otherInputs: updated });
  };

  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const next = () => {
    validate();

    const newStep = step + 1;
    setStep(newStep);

    saveToStorage({ step: newStep });
  };

  const back = () => {
    const newStep = step - 1;
    setStep(newStep);

    saveToStorage({ step: newStep });
  };

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const validate = () => {
    let newErrors = {};

    if (!company.companyName) newErrors.companyName = "Company Name is required";
    if (!company.name) newErrors.name = "Name is required";
    if (!company.email) newErrors.email = "Email is required";
    if (!company.mobile) newErrors.mobile = "Mobile is required";

    if (!company.challenges || company.challenges.length < 100) {
      newErrors.challenges = "Minimum 100 characters required";
    }

    questions.forEach(q => {
      if (!answers[q.id]) {
        newErrors[q.id] = "Required";
      }
    });

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);

    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    validate();
  }, [answers, company, otherInputs]);

  const toTitle = (str) => {
    if (!str || typeof str !== "string") return "";
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // -----------------------------
  // SUBMIT
  // -----------------------------
  const submit = async () => {
    if (!validate()) return;

    setLoading(true);

    const formattedAnswers = questions.map(q => ({
      question_id: q.id,
      question: q.question,
      answer:
        answers[q.id] === "other"
          ? otherInputs[q.id]
          : answers[q.id]
    }));

    console.log("formattedAnswers-------_###########", formattedAnswers);

    const res = await API.post("/scan", {
      company,
      answers: formattedAnswers,
      questions,
      turnstileToken
    });

    console.log("API RESPONSE:", res.data);

    const mapped = mapAIReport(res.data.report, company);

    setReport(mapped);
    setLoading(false);

    setShowLeadGate(true);
  };

  // -----------------------------
  // STATES
  // -----------------------------
  if (loading) return <AILoader />;

  if (showLeadGate) {
    return (
      <LeadGate
        company={company}
        setCompany={setCompany}
        report={report}
        onContinue={() => {
          setShowLeadGate(false);
          setShowSuccess(true);
        }}
      />
    );
  }

  if (showSuccess) {
    return (
      <SuccessScreen
        report={report}
        company={company}
        questions={questions}
        answers={answers}
        otherInputs={otherInputs}
        onContinue={() => setShowSuccess(false)}
      />
    );
  }

  if (report) return <ReportDashboard report={report} company={company} />;

  const currentSection = sections?.[step] || "";

  if (!isHydrated) {
    return (
      <div style={{ textAlign: "center", marginTop: 100 }}>
        Loading...
      </div>
    );
  }

  const progress = Math.round(((step + 1) / sections.length) * 100);
  // -----------------------------
  // UI
  // -----------------------------
  if (!isHumanVerified) {
    return (
      <div style={styles.wrapper}>
        <div style={{ textAlign: "center", marginTop: 120 }}>
          <h2>🔒 Verify You Are Human</h2>
          <p style={{ color: "#64748b" }}>
            Please complete verification to continue
          </p>

          <div style={{ marginTop: 20 }}>
            <Turnstile
              onVerify={(token) => {
                setTurnstileToken(token);
                setIsHumanVerified(true);
              }}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div style={styles.headerInner}>

          <img src="./emeis.png" alt="Emeis Technologies" style={styles.logo} />

          <div style={styles.headerText}>
            <div style={styles.badge}>⚡ AI Powered</div>

            <h1 style={styles.title}>AI Growth Scanner</h1>

            <p style={styles.subtitle}>
              Discover how AI can transform your business in minutes
            </p>
          </div>

          <div style={styles.saveBadge}>
            {saveStatus === "saving" ? "Saving..." : "Saved ✓"}
          </div>

        </div>
      </div>

      <div style={styles.progressBar}>
        <motion.div
          style={styles.progressFill}
          animate={{ width: `${progress}%` }}
        />
      </div>

      <div style={styles.stepperScroll}>
        <div style={styles.stepper}>
          {sections.map((s, i) => (
            <div
              key={i}
              style={{
                ...styles.stepItem,
                cursor: "pointer",
                opacity: i <= step ? 1 : 0.6
              }}
              onClick={() => handleStepClick(i)}
            >
              <div
                style={{
                  ...styles.stepCircle,
                  background: i < step
                    ? "#22c55e"
                    : i === step
                      ? "#6366f1"
                      : "#e5e7eb",
                  color: "#fff"
                }}
              >
                {i < step ? "✓" : i + 1}
              </div>

              <span
                style={{
                  ...styles.stepLabel,
                  color: i === step ? "#4f46e5" : "#64748b",
                  fontWeight: i === step ? 600 : 400
                }}
              >
                {toTitle(s)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          style={styles.card}
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.98 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
        >
          <h2 style={styles.sectionTitle}>{toTitle(currentSection)}</h2>
          <div style={styles.divider} />

          {/* Company */}
          {step === 0 && (
            <div style={styles.grid}>
              {["companyName", "name", "email", "mobile", "website"].map(field => (
                <div key={field} style={styles.companyField}>
                  <label style={styles.label}>{toTitle(field)}</label>

                  <input
                    value={company[field] || ""}
                    placeholder={`Enter ${toTitle(field)}`}
                    style={styles.inputFull}
                    onChange={e => {
                      const updated = {
                        ...company,
                        [field]: e.target.value
                      };

                      setCompany(updated);
                      saveToStorage({ company: updated });
                    }}
                    onFocus={(e) => {
                      e.target.style.border = "1px solid #6366f1";
                      e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.border = "1px solid #e5e7eb";
                      e.target.style.boxShadow = "none";
                    }}
                  />

                  {errors[field] && <p style={styles.error}>{errors[field]}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Questions */}
          {step > 0 &&
            grouped[currentSection]?.map(q => {
              const opts = Array.isArray(q.options)
                ? q.options
                : JSON.parse(q.options || "[]");

              return (
                <div
                  key={q.id}
                  style={{
                    ...styles.question,
                    zIndex: openDropdown === q.id ? 999 : 1
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.04)";
                  }}
                >
                  <label>{q.question}</label>

                  <div style={styles.dropdown}>
                    <div
                      style={styles.dropdownHeader}
                      onClick={() => toggleDropdown(q.id)}
                    >
                      {answers[q.id]
                        ? toTitle(answers[q.id])
                        : "Please Select Option"}
                    </div>

                    {openDropdown === q.id && (
                      <div style={styles.dropdownList}>
                        {opts.map((o, i) => {
                          const key = `${q.id}-${i}`;
                          const isHovered = hoveredItem === key;

                          return (
                            <div
                              key={i}
                              style={{
                                ...styles.dropdownItem,
                                background: isHovered ? "#eef2ff" : "#fff",
                                color: isHovered ? "#4338ca" : "#4f46e5",
                                transform: isHovered ? "translateX(4px)" : "translateX(0px)",
                                transition: "all 0.15s ease"
                              }}
                              onMouseEnter={() => setHoveredItem(key)}
                              onMouseLeave={() => setHoveredItem(null)}
                              onClick={() => {
                                handleSelect(q.id, o);
                                setOpenDropdown(null);
                              }}
                            >
                              {o === "other" ? "Other (Specify)" : toTitle(o)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {errors[q.id] && <p style={styles.error}>{errors[q.id]}</p>}

                  {answers[q.id] === "other" && (
                    <div style={styles.otherWrapper}>
                      <input
                        value={otherInputs[q.id] || ""}
                        autoFocus
                        placeholder="Please specify..."
                        style={styles.otherInput}
                        onChange={(e) => handleOther(q.id, e.target.value)}
                      />
                    </div>
                  )}
                </div>
              );
            })}

          {/* Challenges */}
          {step === sections.length - 1 && (
            <div>
              <label>Current Challenges</label>

              <textarea
                value={company.challenges || ""}
                style={styles.textareaFull}
                placeholder="Describe Your Business Challenges..."
                onChange={e => {
                  const updated = {
                    ...company,
                    challenges: e.target.value
                  };

                  setCompany(updated);
                  saveToStorage({ company: updated });
                }}
              />

              <p style={{ fontSize: 12 }}>
                {company.challenges.length}/100 characters
              </p>

              {errors.challenges && (
                <p style={styles.error}>{errors.challenges}</p>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div style={styles.nav}>
        {step > 0 && (
          <button style={{ ...styles.button, ...styles.secondaryBtn }} onClick={back}>
            Back
          </button>
        )}

        {step < sections.length - 1 ? (
          <button style={{ ...styles.button, ...styles.primaryBtn }} onClick={next}>
            Next →
          </button>
        ) : (
          <button
            onClick={submit}
            style={{
              ...styles.button,
              ...styles.primaryBtn,
              opacity: !isValid ? 0.5 : 1
            }}
          >
            Generate AI Report 🚀
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f8fafc, #eef2ff)",
    fontFamily: "Inter, sans-serif",
    padding: "40px 20px"
  },

  container: {
    maxWidth: 900,
    margin: "0 auto"
  },

  header: {
    textAlign: "center",
    marginBottom: 10,
    backdropFilter: "blur(10px)"
  },

  companyField: {
    background: "rgba(248,250,252,0.9)",
    border: "1px solid #eef2ff",
    boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
    transition: "all 0.2s",
    width: "98%",
    padding: isMobile ? 12 : 16,
    borderRadius: 14
  },

  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: "#475569"
  },

  divider: {
    height: 1,
    background: "linear-gradient(90deg,#6366f1,#e5e7eb)",
    marginBottom: 20,
    borderRadius: 999
  },

  dropdownList: {
    position: "absolute",
    width: "100%",
    maxHeight: 240,
    overflowY: "auto",
    background: "#fff",
    borderRadius: 12,
    marginTop: 6,
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    zIndex: 9999, // 🔥 highest layer
    border: "1px solid #eef2ff"
  },

  dropdown: {
    position: "relative",
    marginTop: 8,
    zIndex: 1000
  },

  error: {
    color: "red",
    fontSize: 12,
    marginTop: 4
  },

  inputFull: {
    width: "100%",
    border: "1px solid #e5e7eb",
    background: "#fff",
    boxSizing: "border-box",
    transition: "all 0.2s",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
    padding: isMobile ? "12px" : "16px 18px",
    borderRadius: 12,
    fontSize: isMobile ? 13 : 15
  },

  otherWrapper: {
    marginTop: 8,
    width: "100%"
  },

  otherInput: {
    width: "97%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #6366f1",
    background: "#f8fafc",
    fontSize: 14,
    outline: "none",
    transition: "all 0.2s"
  },

  dropdownHeader: {
    padding: "14px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    background: "#fff"
  },

  dropdownItem: {
    padding: "12px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 14
  },

  textareaFull: {
    width: "96%",
    padding: "16px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    minHeight: 140,
    resize: "vertical",
    marginTop: 10,
    fontSize: 14
  },

  progressBar: {
    height: 6,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden",
    marginTop: 20
  },

  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg,#6366f1,#8b5cf6)"
  },

  grid: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%"
  },

  input: {
    padding: "14px 16px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    fontSize: 14,
    outline: "none",
    transition: "all 0.2s",
    background: "#fff",
    width: "100%",
  },

  textarea: {
    gridColumn: "span 2",
    padding: "14px 16px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    marginTop: 10,
    minHeight: 100
  },

  question: {
    marginBottom: 20,
    padding: "18px",
    borderRadius: 16,
    background: "rgba(248,250,252,0.9)",
    border: "1px solid #eef2ff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    transition: "all 0.2s ease",
    position: "relative",
    overflow: "visible",
    zIndex: 1
  },

  select: {
    width: "100%",
    padding: "14px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    marginTop: 8
  },

  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    flexDirection: isMobile ? "column" : "row",
    marginTop: 20
  },

  button: {
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    minWidth: 140,
    width: isMobile ? "100%" : "auto",
    padding: "14px",
    fontSize: 14
  },

  primaryBtn: {
    background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
    color: "#fff",
    boxShadow: "0 10px 20px rgba(99,102,241,0.3)"
  },

  secondaryBtn: {
    background: "#fff",
    border: "1px solid #e5e7eb"
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },

  popup: {
    background: "#fff",
    padding: 30,
    borderRadius: 16,
    textAlign: "center",
    width: 400
  },

  badge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    background: "#eef2ff",
    color: "#4f46e5",
    marginBottom: 6,
    fontWeight: 600
  },

  headerInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: isMobile ? "16px 12px" : "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? 10 : 20
  },

  headerText: {
    textAlign: "center",
    flex: 1
  },

  title: {
    fontSize: isMobile ? 26 : 36,
    fontWeight: 800,
    lineHeight: 1.2,
    background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },

  subtitle: {
    fontSize: isMobile ? 13 : 16,
    color: "#64748b"
  },

  logo: {
    width: isMobile ? 70 : 120
  },

  saveBadge: {
    fontSize: 11,
    marginTop: isMobile ? 6 : 0
  },

  stepperScroll: {
    overflowX: "auto",
    padding: "10px 8px"
  },

  stepper: {
    display: "flex",
    gap: 8,
    minWidth: "max-content",
    justifyContent: "center",
  },

  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: isMobile ? "6px 10px" : "8px 12px",
    borderRadius: 999,
    background: "#f1f5f9",
    whiteSpace: "nowrap",
    fontSize: isMobile ? 11 : 12
  },

  stepCircle: {
    width: isMobile ? 20 : 28,
    height: isMobile ? 20 : 28,
    fontSize: isMobile ? 10 : 12,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  stepLabel: {
    fontSize: isMobile ? 10 : 12
  },

  card: {
    marginTop: 20,
    padding: isMobile ? 16 : 30,
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
    maxWidth: 900,
    marginLeft: "auto",
    marginRight: "auto"
  },

  sectionTitle: {
    fontSize: isMobile ? 20 : 26,
    fontWeight: 700,
    color: "#4f46e5"
  }
};